import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  ColumnDef,
  flexRender,
  SortingState,
  PaginationState,
} from "@tanstack/react-table";
import { Image } from "react-bootstrap";
import cn from 'classnames';
import React, { useEffect, useMemo, useState } from "react";
import { ASSESTS } from "../../../../../../../constant/assest";
import CustomButton, { ButtonFont, ButtonSize, ButtonType } from "../../../../../../../component/common/CustomButton/CustomButton";
import Pagination from "../../../../../../../component/UI/Pagination/Pagination";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../../../../../../../redux/store";
import { fetchUserLoginHistory } from "../../../../../../../redux/slices/portfolio/actions";
import { LoginHistoryItem } from "../../../../../../../redux/slices/portfolio/types";
import TableSkeleton from "../../../../../../../component/common/TableSkeleton";
import { format } from "date-fns";
import { usePagination } from "../../../../../../../hooks/usePagination";

interface AnglersSettingsTableProps {
  userId: string;
}

const AnglersSettingsTable: React.FC<AnglersSettingsTableProps> = ({ userId }) => {
  const dispatch = useDispatch<AppDispatch>();
  const { loginHistoryList, loginHistoryListLoading } = useSelector((state: RootState) => state.portfolio);

  const columns = useMemo<ColumnDef<LoginHistoryItem>[]>(() => [
    { header: "Device", accessorKey: "device" },
    { header: "Location", accessorKey: "location" },
    { header: "Timestamp", 
      accessorKey: "loginAt",
      cell: (info) => {
        const date = info.getValue<string>();
        return format(new Date(date), "yyyy-MM-dd hh:mm a");
      },
    },
    { header: "IP Address", accessorKey: "ipAddress" },
    {
      header: "Status",
      accessorKey: "status",
      cell: (info) => {
        const value = info.getValue<string>();
        return (
          <span
            className={cn(
              'statusBadge',
              value.toLowerCase() === "success" ? 'success' : 'failed'
            )}
          >
            {value}
          </span>
        );
      },
    },
  ], []);

  const [sorting, setSorting] = useState<SortingState>([]);
  const [{ pageIndex, pageSize }, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: 5,
  });

  useEffect(() => {
    if (userId) {
      dispatch(fetchUserLoginHistory({ userId, page: pageIndex + 1, limit: pageSize }));
    }
  }, [dispatch, userId, pageIndex, pageSize]);

  const data = useMemo(() => loginHistoryList?.list || [], [loginHistoryList]);
  const pageCount = loginHistoryList?.totalPages || 0;
  const totalEntries = loginHistoryList?.total || 0;
  const table = useReactTable({
    data,
    columns,
    state: {
      sorting,
      pagination: {
        pageIndex,
        pageSize,
      },
    },
    pageCount: pageCount,
    manualPagination: true,
    onSortingChange: setSorting,
    onPaginationChange: setPagination,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    sortDescFirst: false,
  });

   const paginationGroup = usePagination({
      currentPageIndex: pageIndex,
      totalPages: pageCount,
    });
  
    const pageNumbersElements = paginationGroup.map((page, index) => {
      if (typeof page === 'string') {
        return <span key={`ellipsis-${index}`} className={'pageNumberBtn'} style={{cursor: 'default'}}>{page}</span>;
      }
  
      const pIndex = page;
      return (
        <CustomButton
          key={pIndex}
          onClick={() => table.setPageIndex(pIndex)}
          disabled={pageIndex === pIndex}
          btnType={
            pageIndex === pIndex
              ? ButtonType.ACTIVENOSTYLE
              : ButtonType.NOSTYLE
          }
          btnSize={ButtonSize.NORMAL}
          btnFont={
            pageIndex === pIndex
              ? ButtonFont.BOLD
              : ButtonFont.NORMAL
          }
          className={cn(
            'pageNumberBtn',
            pageIndex === pIndex && 'activePageNumberBtn'
          )}
        >
          {pIndex + 1}
        </CustomButton>
      );
    });

  return (
    <div className={'tableContainer'}>
      <div className={'tableWrapper'}>
        <table>
          <thead>
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map((header) => {
                  const isActionColumn = typeof header.column.columnDef.header === "string" && header.column.columnDef.header === "Action";
                  return (
                    <th
                      key={header.id}
                      onClick={header.column.getToggleSortingHandler()}
                      style={{
                        cursor: header.column.getCanSort() ? "pointer" : "default"
                      }}
                      className={cn(header.column.columnDef.id && `th-${header.column.columnDef.id}`, isActionColumn ? 'text-center' : undefined)}
                    >
                      {flexRender(header.column.columnDef.header, header.getContext())}
                      {header.column.getCanSort() &&
                        <span className={cn('tableShorting')}>
                          <Image
                            src={header.column.getIsSorted() === "asc" ? ASSESTS.ASSENDING : header.column.getIsSorted() === "desc" ? ASSESTS.DECENDING : ASSESTS.SHORT}
                            alt="Short"
                          />
                        </span>
                      }
                    </th>
                  );
                })}
              </tr>
            ))}
          </thead>
          <tbody>
            {loginHistoryListLoading === 'pending' ? (
              <TableSkeleton rows={pageSize} columns={ columns.length }/>
            ) : loginHistoryList?.total === 0 ? (
                <tr>
                  <td colSpan={columns.length} className={'noResultsFound'}>
                    No results found
                  </td>
                </tr>
            ) : (
              table.getRowModel().rows.map((row) => (
                <tr key={row.id}>
                  {row.getVisibleCells().map((cell) => {
                    const isActionColumn = typeof cell.column.columnDef.header === "string" && cell.column.columnDef.header === "Action";

                    return (
                      <td
                        key={cell.id}
                        className={cn(cell.column.columnDef.id && `td-${cell.column.columnDef.id}`, isActionColumn ? 'text-center' : undefined)}
                      >
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </td>
                    );
                  })}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
      <Pagination
        enteriesShows={`${totalEntries ? pageIndex * pageSize + 1 : 0 }-${Math.min((pageIndex + 1) * pageSize, totalEntries)} of ${totalEntries} Entries`}

        pageNumbers= {[
          pageCount > 1 && (
            <CustomButton
              key="prev"
              onClick={() => table.previousPage()}
              disabled={!table.getCanPreviousPage()}
              btnType={ButtonType.NOSTYLE}
              className={'pageNumberBtn'}
            >
              Prev
            </CustomButton>
          ),
          ...pageNumbersElements,
          pageCount > 1 && (
            <CustomButton
              key="next"
              onClick={() => table.nextPage()}
              disabled={!table.getCanNextPage()}
              btnType={ButtonType.NOSTYLE}
              className={'pageNumberBtn'}
            >
              Next
            </CustomButton>
          ),
        ].filter(Boolean)}
        dropDownOptions={[
          { value: "5", label: "5" },
          { value: "10", label: "10" },
          { value: "20", label: "20" },
          { value: "25", label: "25" },
        ]}
        dropdownOnChange={(value) => { setPagination(old => ({...old, pageSize: Number(value), pageIndex: 0})); }}
        dropDownPlaceholder={pageSize.toString()}
      />
    </div>
  );
};

export default React.memo(AnglersSettingsTable);
